import { formatNumber } from "@/design-system/lib/format";

/**
 * PASTE TO FILL's input limits (R16), mirrored from `ListingAgentPasteRules` so
 * a refusal arrives before the round trip. The server is authoritative and
 * re-checks everything — including the image's type from its own first bytes,
 * which this mirror does not read. Held in step by the shared fixture
 * `tests/fixtures/listing-agent-paste-limits.json` (`pasteLimits.parity.test.ts`
 * here, `ListingAgentPasteLimitsParityTests` on the server).
 */
export const MAX_PASTE_TEXT_LENGTH = 20_000;
export const MAX_PASTE_IMAGE_BYTES = 5 * 1024 * 1024;
export const PASTE_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

export const NOTHING_PASTED_MESSAGE =
  "Paste the agent's details as text, or add one image, then try again.";
export const IMAGE_TOO_LARGE_MESSAGE =
  "That image is larger than 5 MB. Crop it to the agent's details, or take a smaller screenshot.";
export const IMAGE_WRONG_TYPE_MESSAGE = "Only PNG, JPEG or WebP images can be read.";
export const NOTHING_FOUND_MESSAGE =
  "Couldn't find contact details in that — try copying more of the page";
export const RATE_LIMITED_MESSAGE =
  "Too many AI requests in the last minute. Wait a moment and try again.";

export function textTooLongMessage(length: number): string {
  return `That paste is ${formatNumber(length)} characters. The limit is 20,000 — copy just the part of the page with the agent's details.`;
}

/** Null when the text may be sent; otherwise the sentence to show. Counted
 *  after trimming, as the server counts. */
export function pasteTextProblem(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return NOTHING_PASTED_MESSAGE;
  if (trimmed.length > MAX_PASTE_TEXT_LENGTH) return textTooLongMessage(trimmed.length);
  return null;
}

/** Null when the file may be sent as the one image; otherwise the sentence. */
export function pasteImageProblem(file: Pick<File, "type" | "size">): string | null {
  if (!(PASTE_IMAGE_TYPES as readonly string[]).includes(file.type.toLowerCase())) {
    return IMAGE_WRONG_TYPE_MESSAGE;
  }
  if (file.size > MAX_PASTE_IMAGE_BYTES) return IMAGE_TOO_LARGE_MESSAGE;
  return null;
}

/** The file's bytes as raw base64 (the server takes no `data:` prefix). */
export function readFileAsBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("The image could not be read."));
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}
